<script>
import { useVuelidate } from '@vuelidate/core';
import { useAlert } from 'dashboard/composables';
import { required, minLength, email } from '@vuelidate/validators';
import { useBranding } from 'shared/composables/useBranding';
import { mapGetters } from 'vuex';
import FormInput from '../../../../components/Form/Input.vue';
import { resetPassword } from '../../../../api/auth';
import NextButton from 'dashboard/components-next/button/Button.vue';

export default {
  components: { FormInput, NextButton },
  setup() {
    const { replaceInstallationName } = useBranding();
    return { v$: useVuelidate(), replaceInstallationName };
  },
  data() {
    return {
      credentials: { email: '' },
      resetPassword: {
        message: '',
        showLoading: false,
        hasErrored: false,
      },
      error: '',
    };
  },
  validations() {
    return {
      credentials: {
        email: {
          required,
          email,
          minLength: minLength(4),
        },
      },
    };
  },
  computed: {
    ...mapGetters({ globalConfig: 'globalConfig/get' }),
  },
  methods: {
    getParticleStyle() {
      const size = Math.random() * 4 + 1;
      const left = Math.random() * 100;
      const animationDelay = Math.random() * 20;
      const animationDuration = Math.random() * 20 + 10;
      return {
        width: `${size}px`,
        height: `${size}px`,
        left: `${left}%`,
        animationDelay: `${animationDelay}s`,
        animationDuration: `${animationDuration}s`,
      };
    },
    showAlertMessage(message) {
      // Reset loading, current selected agent
      this.resetPassword.showLoading = false;
      useAlert(message);
    },
    submit() {
      this.resetPassword.hasErrored = false;
      this.resetPassword.showLoading = true;
      resetPassword(this.credentials)
        .then(res => {
          let successMessage = this.$t('RESET_PASSWORD.API.SUCCESS_MESSAGE');
          if (res.data && res.data.message) {
            successMessage = res.data.message;
          }
          this.showAlertMessage(successMessage);
        })
        .catch(error => {
          this.resetPassword.hasErrored = true;
          let errorMessage = this.$t('RESET_PASSWORD.API.ERROR_MESSAGE');
          if (error?.response?.data?.message) {
            errorMessage = error.response.data.message;
          }
          this.showAlertMessage(errorMessage);
        });
    },
  },
};
</script>

<template>
  <main class="relative flex h-screen overflow-hidden new-theme-bg">
    <!-- Grain texture overlay -->
    <div class="grain-overlay" />
    <!-- Left Panel: Branding / Illustration -->
    <section
      class="hidden lg:flex flex-col items-center w-1/2 p-16 pt-64 text-center relative overflow-hidden h-screen z-10 animate-slide-from-left"
    >
      <!-- Vertical Divider Line -->
      <div
        class="absolute right-0 top-1/2 -translate-y-1/2 h-3/4 w-px bg-gradient-to-b from-transparent via-white/50 to-transparent z-20"
      />

      <div
        class="relative z-10 w-full flex flex-col items-center justify-center space-y-8"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-14 drop-shadow-2xl filter brightness-110 animate-float-in"
        />

        <div class="space-y-6">
          <p
            class="text-slate-50 text-xl leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] max-w-md animate-slide-up-delayed"
          >
            {{ $t('RESET_PASSWORD.BRANDING.TAGLINE') }}<br />
            <span class="text-slate-100 font-normal">
              {{ $t('RESET_PASSWORD.BRANDING.SUBTITLE') }}
            </span>
          </p>

          <!-- Glowing orb effect -->
          <div class="relative mt-12">
            <div
              class="w-64 h-64 mx-auto rounded-full bg-gradient-to-tr from-cyan-500/20 via-blue-500/30 to-purple-500/20 blur-3xl opacity-60 animate-pulse-slow"
            />
            <div
              class="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-32 h-32 rounded-full bg-gradient-to-tr from-cyan-400/40 to-blue-400/40 blur-2xl animate-pulse-slower"
            />
          </div>
        </div>
      </div>
    </section>

    <!-- Right Panel: Reset Password Card -->
    <section
      class="flex flex-col justify-center items-center w-full lg:w-1/2 px-3 sm:px-4 md:px-6 lg:px-12 py-6 sm:py-8 md:py-12 lg:py-16 relative overflow-hidden h-screen z-10 animate-slide-from-right"
    >
      <!-- Mobile Logo and Brand Name (visible only on mobile, outside the box) -->
      <div
        class="lg:hidden flex flex-col items-center mb-4 sm:mb-6 animate-fade-in-up w-full max-w-sm sm:max-w-md px-4"
      >
        <img
          :src="globalConfig.logoDark || globalConfig.logo"
          :alt="globalConfig.installationName"
          class="h-10 sm:h-12 mb-2 sm:mb-3 drop-shadow-2xl filter brightness-110"
        />
        <p
          class="text-slate-50 text-xs sm:text-sm leading-relaxed font-light tracking-wide drop-shadow-[0_0_6px_rgba(255,255,255,0.3)] text-center"
        >
          {{ $t('RESET_PASSWORD.BRANDING.TAGLINE') }}<br />
          <span class="text-slate-100 font-normal">
            {{ $t('RESET_PASSWORD.BRANDING.SUBTITLE') }}
          </span>
        </p>
      </div>

      <div
        class="w-full max-w-sm sm:max-w-md bg-white/5 backdrop-blur-4xl rounded-xl sm:rounded-2xl md:rounded-3xl border border-white/10 p-4 sm:p-6 md:p-8 lg:p-10 transition-all duration-300 hover:bg-white/10 hover:border-white/20 mx-2 sm:mx-4 md:mx-0 shadow-2xl shadow-black/40"
        :class="{ 'animate-wiggle': resetPassword.hasErrored }"
      >
        <div class="text-center mb-6 sm:mb-8 animate-fade-in-up">
          <h2
            class="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2 sm:mb-3 md:mb-4 tracking-wide"
          >
            {{ $t('RESET_PASSWORD.TITLE') }}
          </h2>
          <p
            class="text-xs sm:text-sm text-slate-200 drop-shadow-[0_0_4px_rgba(255,255,255,0.2)]"
          >
            {{ replaceInstallationName($t('RESET_PASSWORD.DESCRIPTION')) }}
          </p>
        </div>

        <!-- Reset Password Form -->
        <form
          class="space-y-3 sm:space-y-4 md:space-y-5 transition-all duration-500 animate-fade-in-up-delayed"
          @submit.prevent="submit"
        >
          <!-- Email -->
          <div class="group/input">
            <FormInput
              v-model="credentials.email"
              name="email_address"
              type="text"
              data-testid="email_input"
              :tabindex="1"
              required
              :has-error="v$.credentials.email.$error"
              :error-message="$t('RESET_PASSWORD.EMAIL.ERROR')"
              :placeholder="$t('RESET_PASSWORD.EMAIL.PLACEHOLDER')"
              class="rounded-xl focus-within:ring-2 focus-within:ring-purple-400/70 focus-within:border-purple-400/50 transition-all duration-300 bg-white/5 border-transparent text-white placeholder-gray-300"
              @input="v$.credentials.email.$touch"
            />
          </div>

          <!-- Submit -->
          <NextButton
            lg
            type="submit"
            data-testid="submit_button"
            class="w-full mt-3 sm:mt-4 md:mt-6 bg-gradient-to-r from-violet-600 via-violet-500 to-violet-600 text-white"
            :tabindex="2"
            :label="$t('RESET_PASSWORD.SUBMIT')"
            :disabled="
              v$.credentials.email.$invalid || resetPassword.showLoading
            "
            :is-loading="resetPassword.showLoading"
          />
        </form>

        <p
          class="mt-6 text-center text-sm text-slate-200 drop-shadow-[0_0_4px_rgba(255,255,255,0.2)] animate-fade-in-delayed"
        >
          {{ $t('RESET_PASSWORD.GO_BACK_TO_LOGIN') }}
          <router-link
            to="/auth/login"
            class="text-white hover:text-gray-200 font-medium transition-colors"
          >
            {{ $t('COMMON.CLICK_HERE') }}
          </router-link>
        </p>
      </div>

      <footer
        class="mt-6 sm:mt-8 lg:mt-10 text-center text-xs text-n-slate-10 px-4 animate-fade-in-delayed"
      >
        {{ $t('RESET_PASSWORD.BRANDING.COPYRIGHT') }}
        {{ new Date().getFullYear() }} {{ globalConfig.installationName }}
        <span class="text-n-slate-8 hidden sm:inline"> • </span>
        <br class="sm:hidden" />
        <span class="text-n-slate-8">
          {{ $t('RESET_PASSWORD.BRANDING.FOOTER_TEXT') }}
        </span>
      </footer>
    </section>
  </main>
</template>

<style scoped>
.fade-slide-enter-active {
  transition: all 0.6s ease;
}
.fade-slide-enter-from {
  opacity: 0;
  transform: translateY(10px);
}
.fade-slide-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}

/* Force white text color for form inputs */
:deep(.group\/input input) {
  color: white !important;
}

:deep(.group\/input input::placeholder) {
  color: #9ca3af !important; /* gray-400 */
}

/* Force white color for Email and Password labels */
:deep(.group\/input label) {
  color: white !important;
}

/* Password toggle button positioning */
:deep(.group\/input .relative) {
  position: relative;
}

:deep(.group\/input input) {
  padding-right: 0.75rem !important;
  padding-top: 0.75rem !important;
  padding-bottom: 0.75rem !important;
  padding-left: 0.75rem !important;
  border-color: transparent !important;
  outline-color: transparent !important;
}

:deep(.group\/input input:hover) {
  outline-color: transparent !important;
}

:deep(.group\/input input:focus) {
  border-color: rgb(192 132 252 / 0.5) !important;
  outline-color: rgb(192 132 252 / 0.5) !important;
}

/* Particle animation */
.particle-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  overflow: hidden;
}

.particle {
  position: absolute;
  background: linear-gradient(
    45deg,
    rgba(6, 182, 212, 0.6),
    rgba(59, 130, 246, 0.4)
  );
  border-radius: 50%;
  animation: float linear infinite;
  opacity: 0.7;
}

@keyframes float {
  0% {
    transform: translateY(100vh) rotate(0deg);
    opacity: 0;
  }
  10% {
    opacity: 0.7;
  }
  90% {
    opacity: 0.7;
  }
  100% {
    transform: translateY(-100px) rotate(360deg);
    opacity: 0;
  }
}

/* Grain animation */
@keyframes grain {
  0%,
  100% {
    transform: translate(0, 0);
  }
  10% {
    transform: translate(-5%, -10%);
  }
  20% {
    transform: translate(-15%, 5%);
  }
  30% {
    transform: translate(6%, -25%);
  }
  40% {
    transform: translate(-5%, 25%);
  }
  50% {
    transform: translate(-15%, 10%);
  }
  60% {
    transform: translate(15%, 0%);
  }
  70% {
    transform: translate(0%, 15%);
  }
  80% {
    transform: translate(-10%, -5%);
  }
  90% {
    transform: translate(5%, 20%);
  }
}

/* Wiggle animation for errors */
@keyframes wiggle {
  0%,
  7% {
    transform: rotateZ(0);
  }
  15% {
    transform: rotateZ(-15deg);
  }
  20% {
    transform: rotateZ(10deg);
  }
  25% {
    transform: rotateZ(-10deg);
  }
  30% {
    transform: rotateZ(6deg);
  }
  35% {
    transform: rotateZ(-4deg);
  }
  40%,
  100% {
    transform: rotateZ(0);
  }
}

.animate-wiggle {
  animation: wiggle 0.5s ease-in-out;
}

/* Enhanced hover effects */
/* Removed input hover transform effect */

/* Removed problematic glow effect */

/* Custom gradient for radial backgrounds */
.bg-gradient-radial {
  background: radial-gradient(circle, var(--tw-gradient-stops));
}

/* Mobile optimizations */
@media (max-width: 640px) {
  .particle {
    display: none; /* Hide particles on mobile for better performance */
  }

  .group:hover .group\/input {
    transform: none; /* Disable hover transforms on mobile */
  }

  .group:hover {
    transform: none; /* Disable hover scale on mobile */
  }

  /* Reduce animation intensity on mobile */
  .animate-pulse {
    animation-duration: 3s;
  }

  /* Optimize backdrop blur for mobile */
  .backdrop-blur-xl {
    backdrop-filter: blur(8px);
  }
}

/* Tablet optimizations */
@media (min-width: 641px) and (max-width: 1024px) {
  .particle {
    opacity: 0.5; /* Reduce particle opacity on tablets */
  }
}

/* Attractive Page Animations */
@keyframes slideFromLeft {
  from {
    opacity: 0;
    transform: translateX(-100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes slideFromRight {
  from {
    opacity: 0;
    transform: translateX(100%);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes floatIn {
  from {
    opacity: 0;
    transform: translateY(-30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes slideUpDelayed {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.9);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulseSlow {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 0.8;
    transform: scale(1.05);
  }
}

@keyframes pulseSlower {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(1);
  }
  50% {
    opacity: 0.6;
    transform: scale(1.08);
  }
}

.animate-slide-from-left {
  animation: slideFromLeft 1s ease-out;
}

.animate-slide-from-right {
  animation: slideFromRight 1s ease-out;
}

.animate-fade-in {
  animation: fadeIn 1s ease-out;
}

.animate-float-in {
  animation: floatIn 0.8s ease-out 1s both;
}

.animate-slide-up-delayed {
  animation: slideUpDelayed 0.8s ease-out 1.2s both;
}

.animate-scale-in {
  animation: scaleIn 0.8s ease-out;
}

.animate-fade-in-up {
  animation: fadeInUp 0.6s ease-out 1s both;
}

.animate-fade-in-up-delayed {
  animation: fadeInUp 0.6s ease-out 1.2s both;
}

.animate-fade-in-delayed {
  animation: fadeIn 0.8s ease-out 1.4s both;
}

.animate-pulse-slow {
  animation: pulseSlow 4s ease-in-out infinite;
}

.animate-pulse-slower {
  animation: pulseSlower 6s ease-in-out infinite;
}

/* New theme background - image with grain texture */
.new-theme-bg {
  background: url('/assets/images/auth/login-bg.png') center center / cover
    no-repeat;
  position: relative;
}

/* Grain texture overlay - disabled since image has grain */
.grain-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  opacity: 0;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='1.2' numOctaves='5' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E");
  background-size: 180px 180px;
  animation: grain 6s steps(8) infinite;
  mix-blend-mode: soft-light;
  z-index: 1;
}

/* Grain animation for texture movement */
@keyframes grain {
  0%,
  100% {
    transform: translate(0, 0);
  }
  10% {
    transform: translate(-2%, -2%);
  }
  20% {
    transform: translate(-1%, 1%);
  }
  30% {
    transform: translate(1%, -1%);
  }
  40% {
    transform: translate(-1%, 1%);
  }
  50% {
    transform: translate(-1%, -1%);
  }
  60% {
    transform: translate(1%, 0%);
  }
  70% {
    transform: translate(0%, 1%);
  }
  80% {
    transform: translate(-1%, -1%);
  }
  90% {
    transform: translate(1%, 1%);
  }
}
</style>
